import { inject, Injectable } from '@angular/core';
import { limit, orderBy, where } from '@angular/fire/firestore';
import { Notification } from '@models';
import { map, of, shareReplay, switchMap } from 'rxjs';
import { FirestoreService } from './firestore.service';
import { TenantContextService } from './tenant-context.service';
import { UserService } from './current-user.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationsUserService {
  private readonly firestore = inject(FirestoreService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly userService = inject(UserService);

  readonly notifications$ = this.userService.user$.pipe(
    switchMap((user) => {
      if (!user?.id) return of([] as Notification[]);
      return this.firestore.listDocuments<Notification>(
        this.tenantContext.collections.NOTIFICATIONS,
        where('userId', '==', user.id),
        orderBy('createdAt', 'desc'),
        limit(30),
      );
    }),
    shareReplay(1),
  );

  /** Admin notifications not yet read — shown in the "Pendientes" tab. */
  readonly unreadNotifications$ = this.notifications$.pipe(
    map((notifications) => notifications.filter((n) => !n.read)),
    shareReplay(1),
  );

  /** Admin notifications already read — shown in the "Anteriores" tab. */
  readonly readNotifications$ = this.notifications$.pipe(
    map((notifications) => notifications.filter((n) => n.read)),
    shareReplay(1),
  );

  markAsRead(notification: Notification) {
    if (notification.read) return Promise.resolve();
    // updateFields (no `id` en el payload) para no romper la regla Firestore
    // que restringe el update del dueño a hasOnly(['read', 'updatedAt']). Las
    // notificaciones creadas por las CF (`.add()`) no tienen campo `id`, así que
    // escribirlo lo añadiría a affectedKeys y denegaría el update.
    return this.firestore.updateFields<Notification>(
      this.tenantContext.collections.NOTIFICATIONS,
      notification.id,
      { read: true },
    );
  }
}
